import { useEffect, useState, type ReactNode } from "react";
import { detectCountry, isBlockedCountry } from "@/shared/lib/geoBlock";

interface GeoBlockGateProps {
  children: ReactNode;
}

/**
 * Top-level gate that blocks visitors from restricted countries.
 * Renders nothing until country detection resolves to avoid a flash of content.
 * On detection failure, fails open (allows access) to avoid blocking legitimate users.
 */
export function GeoBlockGate({ children }: GeoBlockGateProps) {
  const [status, setStatus] = useState<"checking" | "allowed" | "blocked">(
    "checking",
  );

  useEffect(() => {
    let cancelled = false;
    detectCountry().then((country) => {
      if (cancelled) return;
      setStatus(isBlockedCountry(country) ? "blocked" : "allowed");
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (status === "checking") {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-background" />
    );
  }

  if (status === "blocked") {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-background px-6">
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-semibold text-foreground mb-3">
            Service unavailable
          </h1>
          <p className="text-muted-foreground">
            This service is not available in your region.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
